import { ScrollBar } from '@yes.technology/react-toolkit'
import styled, { css } from 'styled-components'
import { DEFAULT_CARD_HEIGHT } from './Gantt.utils'

const COLOR_BORDER_PRIMARY = '#788B9E'
const COLOR_BACKGROUND_WHITE = '#ffffff'
const COLOR_TEXT_PRIMARY = '#303032'
const COLOR_MONTH_ACCENT = '#abbed1'
const COLOR_CURRENT_MONTH = '#c7deff'
const COLOR_ROW_BORDER = '#e6eaef'
const COLOR_BACKGROUND_STRIPED = '#f7f8fa'
const COLOR_MILESTONE = '#ff7373'
const COLOR_CARD_TEXT = '#1f2a37'
const COLOR_FOCUS_OUTLINE = '#3b82f6'
const COLOR_CURRENT_MONTH_HIGHLIGHT = 'rgba(199, 222, 255, 0.25)'
export const ARROW_STROKE_COLOR = '#002B66'
export const SELECTED_CARD_BORDER_COLOR = '#2563eb'

const Z_INDEX_TIMELINE_MILESTONE_LINE = 2
const Z_INDEX_CARD_BUTTON = 3
const Z_INDEX_ARROWS_SVG = 4

export const SIDEBAR_WIDTH = 70
export const MIN_MONTH_WIDTH = 120
export const MONTH_COLUMN_GAP = 4

export const GanttHiddenHorizontalScrollbar = styled(ScrollBar)`
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

export const GanttBodyTimelineScroll = styled(ScrollBar)`
  scrollbar-gutter: stable;
`

export const GanttRoot = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: ${COLOR_BACKGROUND_WHITE};
  overflow: hidden;
`

export const GanttHeader = styled.div`
  display: flex;
`

export const SidebarHeader = styled.div`
  height: 24px;
  width: ${SIDEBAR_WIDTH}px;
  min-width: ${SIDEBAR_WIDTH}px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_TEXT_PRIMARY};
  display: flex;
  align-items: center;
`

export const MonthHeaderGrid = styled.div<{ $columns: string; $width: number }>`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  column-gap: ${MONTH_COLUMN_GAP}px;
  width: ${({ $width }) => `${$width}px`};
  min-width: ${({ $width }) => `${$width}px`};
`

export const MonthHeaderCell = styled.div<{ $isCurrent: boolean }>`
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  line-height: 24px;
  font-weight: 600;
  color: ${COLOR_TEXT_PRIMARY};
  border-bottom: 2px solid ${COLOR_MONTH_ACCENT};
  background-color: ${COLOR_BACKGROUND_WHITE};

  ${({ $isCurrent }) =>
    $isCurrent &&
    css`
      background-color: ${COLOR_CURRENT_MONTH};
    `}
`

export const GanttBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 240px;
`

export const SidebarBody = styled.div`
  display: flex;
  flex-direction: column;
  width: ${SIDEBAR_WIDTH}px;
  min-width: ${SIDEBAR_WIDTH}px;
`

export const SidebarCategoryGroup = styled.div<{ $height: number }>`
  display: flex;
  height: ${({ $height }) => `${$height}px`};
  border-bottom: 1px solid ${COLOR_ROW_BORDER};

  &:last-child {
    border-bottom: none;
  }
`

export const SidebarCategoryColumn = styled.div<{
  $backgroundColor: string
  $labelColor: string
}>`
  width: ${SIDEBAR_WIDTH}px;
  max-width: ${SIDEBAR_WIDTH}px;
  min-width: ${SIDEBAR_WIDTH}px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: ${({ $labelColor }) => $labelColor};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  white-space: nowrap;
  padding: 12px 0;
`

export const SidebarRowsColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

export const SidebarRow = styled.div<{ $isStriped: boolean; $height: number }>`
  height: ${({ $height }) => `${$height}px`};
  padding: 0 12px;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: ${COLOR_TEXT_PRIMARY};
  background-color: ${({ $isStriped }) =>
    $isStriped ? COLOR_BACKGROUND_STRIPED : COLOR_BACKGROUND_WHITE};
  white-space: nowrap;
`

export const TimelineCanvas = styled.div<{ $height: number; $width: number }>`
  position: relative;
  width: ${({ $width }) => `${$width}px`};
  min-width: ${({ $width }) => `${$width}px`};
  min-height: ${({ $height }) => `${$height}px`};
`

export const GridRows = styled.div`
  position: relative;
`

export const GridRow = styled.div<{
  $height: number
  $isStriped: boolean
  $columns?: string
  $width?: number
}>`
  display: grid;
  grid-template-columns: ${({ $columns }) =>
    $columns ?? `repeat(12, minmax(${MIN_MONTH_WIDTH}px, 1fr))`};
  column-gap: ${MONTH_COLUMN_GAP}px;
  width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
  min-width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
  min-height: ${({ $height }) => `${$height}px`};
  background-color: ${({ $isStriped }) =>
    $isStriped ? COLOR_BACKGROUND_STRIPED : COLOR_BACKGROUND_WHITE};
`

export const GridCell = styled.div`
  &:not(:first-child) {
    border-left: 4px solid ${COLOR_BACKGROUND_WHITE};
  }
`

export const CurrentMonthHighlight = styled.div<{
  $left: number
  $width: number
}>`
  position: absolute;
  top: 0;
  left: ${({ $left }) => `${$left}px`};
  width: ${({ $width }) => `${$width}px`};
  height: 100%;
  background-color: ${COLOR_CURRENT_MONTH_HIGHLIGHT};
  pointer-events: none;
`

export const TimelineMilestoneLine = styled.div<{ $left: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => `${$left}px`};
  width: 2px;
  background-color: ${COLOR_MILESTONE};
  pointer-events: none;
  z-index: ${Z_INDEX_TIMELINE_MILESTONE_LINE};
  transform: translateX(-50%);
`

export const CardButton = styled.button<{
  $left: number
  $top: number
  $width: number
}>`
  position: absolute;
  left: ${({ $left }) => `${$left}px`};
  top: ${({ $top }) => `${$top}px`};
  width: ${({ $width }) => `${$width}px`};
  height: ${DEFAULT_CARD_HEIGHT}px;
  border: 1px solid transparent;
  border-radius: 6px;
  color: ${COLOR_CARD_TEXT};
  font-size: 12px;
  text-align: left;
  padding: 4px 8px;
  cursor: pointer;
  z-index: ${Z_INDEX_CARD_BUTTON};
  overflow: hidden;

  &:hover {
    filter: brightness(0.97);
  }

  &:focus-visible {
    outline: 2px solid ${COLOR_FOCUS_OUTLINE};
    outline-offset: 1px;
  }
`

export const ArrowsSvg = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: ${Z_INDEX_ARROWS_SVG};
`

export const GanttFooter = styled.div`
  display: flex;
  border-top: 2px solid ${COLOR_BORDER_PRIMARY};
  background-color: ${COLOR_BACKGROUND_STRIPED};
`

export const SidebarFooter = styled.div`
  width: ${SIDEBAR_WIDTH}px;
  min-width: ${SIDEBAR_WIDTH}px;
  background-color: ${COLOR_BACKGROUND_STRIPED};
  color: ${COLOR_TEXT_PRIMARY};
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  padding: 0 12px;
  height: 44px;
`

export const GanttFooterTimelineFrame = styled.div<{ $width: number }>`
  box-sizing: border-box;
  position: relative;
  width: ${({ $width }) => `${$width}px`};
  min-width: ${({ $width }) => `${$width}px`};
  min-height: 44px;
  height: 100%;
`

export const MilestoneTrack = styled.div<{ $width: number }>`
  position: absolute;
  inset: 0;
  width: ${({ $width }) => `${$width}px`};
  min-width: ${({ $width }) => `${$width}px`};
  pointer-events: none;
`

export const MilestoneLine = styled.div<{ $left: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => `${$left}px`};
  width: 2px;
  background-color: ${COLOR_MILESTONE};
  transform: translateX(-50%);
`

export const MilestoneIndicator = styled.div<{ $left: number }>`
  position: absolute;
  left: ${({ $left }) => `${$left - 12}px`};
  top: 50%;
  transform: translate(calc(-100% - 8px), -50%);
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  flex-shrink: 0;
  width: max-content;
  max-width: none;
  color: ${COLOR_TEXT_PRIMARY};
  white-space: nowrap;
`

export const MilestoneLabel = styled.span`
  display: inline-block;
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: inherit;
`

export const MilestoneDot = styled.span<{ $left: number }>`
  position: absolute;
  left: ${({ $left }) => `${$left}px`};
  top: 50%;
  transform: translate(-50%, -50%);
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: ${COLOR_MILESTONE};
  flex-shrink: 0;
`
